import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import type { IPartyState } from '../interface/state/IParty';

// Standalone client for api.core's ballot resolver,
// `GET /api/v1/Election/{electionId}/ballot-parties`. Same headers the Redux thunk
// sends, but no 401->logout interceptor and no global error toast: the resolver is
// not deployed everywhere yet, and a miss here must quietly fall back to the
// all-parties list rather than cost the agent their vote-entry screen.
const ballotPartiesApi = axios.create({ baseURL: COREAPI });

ballotPartiesApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

/** 'fallback' = no ballot configured for the election; the server returned every active party. */
export type BallotSource = 'election' | 'attribute' | 'fallback';

export interface IBallotParty {
	id: number;
	shortName: string;
	longName: string;
	logo: string;
	isActive: boolean;
	ballotOrder: number | null;
}

export interface IBallotPartiesData {
	electionId: number;
	electionCycleId: number | null;
	source: BallotSource;
	parties: IBallotParty[];
}

/** A ballot party in the app's party-list shape, keeping its position for display ordering. */
export type IBallotPartyRow = IPartyState & Pick<IBallotParty, 'ballotOrder'>;

export type BallotPartiesOutcome = { kind: 'ballot'; source: BallotSource; parties: IBallotPartyRow[] } | { kind: 'unavailable' };

/**
 * Where the party list on the vote-entry screen came from.
 * - `none`: no election id was known, so the resolver was never asked (pre-resolver behaviour).
 * - `unavailable`: the resolver was asked and failed (404/405 before rollout, network, bad body);
 *   the all-parties list was used instead.
 */
export type PartyListSource = BallotSource | 'unavailable' | 'none';

const SOURCES: readonly BallotSource[] = ['election', 'attribute', 'fallback'];

/** Ballot order ascending, unordered parties last; ties keep the server's order. */
export const orderBallotParties = <T extends Pick<IBallotParty, 'ballotOrder'>>(parties: readonly T[]): T[] =>
	parties
		.map((party, index) => ({ party, index }))
		.sort((a, b) => {
			const ao = typeof a.party.ballotOrder === 'number' ? a.party.ballotOrder : Infinity;
			const bo = typeof b.party.ballotOrder === 'number' ? b.party.ballotOrder : Infinity;
			if (ao !== bo) return ao < bo ? -1 : 1;
			return a.index - b.index;
		})
		.map(({ party }) => party);

/**
 * Classify a resolver response. Anything other than a successful envelope carrying a
 * known source and a non-empty party list is `unavailable`, and the caller falls back
 * to the all-parties list — an empty ballot must never leave the agent with nothing
 * to type into.
 */
export const interpretBallotResponse = (httpStatus: number, body: unknown): BallotPartiesOutcome => {
	if (httpStatus < 200 || httpStatus >= 300) return { kind: 'unavailable' };
	if (!body || typeof body !== 'object') return { kind: 'unavailable' };
	const envelope = body as { isSuccessful?: unknown; data?: Partial<IBallotPartiesData> | null };
	if (envelope.isSuccessful === false) return { kind: 'unavailable' };
	const data = envelope.data;
	if (!data || !SOURCES.includes(data.source!) || !Array.isArray(data.parties)) return { kind: 'unavailable' };

	const parties = orderBallotParties(data.parties.filter((p) => p && typeof p.id === 'number')).map((p) => ({
		id: p.id,
		shortName: p.shortName,
		longName: p.longName,
		logo: p.logo,
		ballotOrder: typeof p.ballotOrder === 'number' ? p.ballotOrder : null,
	}));
	if (!parties.length) return { kind: 'unavailable' };
	return { kind: 'ballot', source: data.source!, parties };
};

/** True when the agent should be told the list is every active party rather than this election's ballot. */
export const isAllPartiesList = (source: PartyListSource): boolean => source === 'fallback' || source === 'unavailable';

/**
 * The one decision every party-list consumer makes: ask the resolver when the election
 * is known, otherwise (or when it misses) hand over to the existing all-parties load.
 * Exactly one of the two callbacks runs.
 */
export const resolvePartyList = async ({
	electionId,
	onBallot,
	onAllParties,
	fetchBallot = getBallotParties,
}: {
	electionId: number | null | undefined;
	onBallot: (source: BallotSource, parties: IBallotPartyRow[]) => void;
	onAllParties: (source: Extract<PartyListSource, 'none' | 'unavailable'>) => void;
	fetchBallot?: (electionId: number) => Promise<BallotPartiesOutcome>;
}): Promise<void> => {
	if (!electionId || electionId <= 0) {
		onAllParties('none');
		return;
	}
	const outcome = await fetchBallot(electionId).catch((): BallotPartiesOutcome => ({ kind: 'unavailable' }));
	if (outcome.kind === 'ballot') onBallot(outcome.source, outcome.parties);
	else onAllParties('unavailable');
};

export const getBallotParties = async (electionId: number): Promise<BallotPartiesOutcome> => {
	try {
		const res = await ballotPartiesApi.get<unknown>(`/api/v1/Election/${electionId}/ballot-parties`, {
			validateStatus: () => true,
		});
		let body = res.data;
		// The core service sometimes returns JSON with a Content-Type axios won't parse.
		if (typeof body === 'string' && body) {
			try {
				body = JSON.parse(body);
			} catch {
				// Not JSON (e.g. the SPA's HTML) — interpretBallotResponse rejects it.
			}
		}
		return interpretBallotResponse(res.status, body);
	} catch {
		return { kind: 'unavailable' };
	}
};
